import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, RefreshCw, Loader2, AlertTriangle, ShieldCheck, ListChecks, TrendingUp } from 'lucide-react';

const API = '/api';

const URGENCY_COLOR = {
  critical: 'bg-rose-100 text-rose-900 border-rose-200',
  high: 'bg-orange-100 text-orange-900 border-orange-200',
  elevated: 'bg-amber-100 text-amber-900 border-amber-200',
  normal: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  closed: 'bg-gray-100 text-gray-700 border-gray-200',
};

const URGENCY_LABEL = {
  critical: '> 72u',
  high: '> 48u',
  elevated: '> 24u',
  normal: 'Vers',
  closed: 'Gesloten',
};

const BLOCKER_LABEL = {
  klantnaam_ontbreekt: 'Naam ontbreekt',
  email_ontbreekt: 'E-mail ontbreekt',
  telefoon_ontbreekt: 'Telefoon ontbreekt',
  klantadres_ontbreekt: 'Adres ontbreekt',
  provider_match_ontbreekt: 'Installateur ontbreekt',
  provider_niet_goedgekeurd: 'Provider niet goedgekeurd',
};

export default function InstallerRevenueTab({ token }) {
  const [data, setData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterUrgency, setFilterUrgency] = useState('all');
  const [includeClosed, setIncludeClosed] = useState(false);

  const authH = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.set('category', filterCategory);
      if (filterUrgency !== 'all') params.set('urgency', filterUrgency);
      if (includeClosed) params.set('include_closed', 'true');
      const [q, k] = await Promise.all([
        axios.get(`${API}/admin/installer-revenue/queue?${params.toString()}`, { headers: authH }),
        axios.get(`${API}/admin/installer-revenue/kpis`, { headers: authH }),
      ]);
      setData(q.data);
      setKpis(k.data);
    } catch (e) {
      toast.error('Kon installer revenue queue niet laden');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterCategory, filterUrgency, includeClosed]);

  useEffect(() => { load(); }, [load]);

  const generateTodo = async (item, customAction) => {
    setBusy(`todo-${item.id}`);
    try {
      const { data: r } = await axios.post(`${API}/admin/installer-revenue/generate-todo`, {
        source: item.source,
        id: item.id,
        action_label: customAction || item.recommended_action,
      }, { headers: authH });
      if (r.created) toast.success(`To-do aangemaakt: ${item.recommended_action}`);
      else toast.info('To-do bestond al (geen duplicaat)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'To-do aanmaken mislukt');
    }
    setBusy(null);
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }
  if (!data) return <div className="text-center py-8 text-sm text-muted-foreground">Geen data.</div>;

  const items = data.items || [];
  const summary = data.summary || {};
  const categories = data.categories_meta || {};

  return (
    <div className="space-y-4" data-testid="installer-revenue-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-base sm:text-lg font-medium flex items-center gap-2">
          <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          Installer Revenue Queue (Blok 4)
        </h2>
        <button onClick={load} disabled={loading} className="px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted flex items-center gap-1.5" data-testid="ir-refresh">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-[11px] sm:text-xs text-blue-900">
        <strong>Approval-first.</strong> Geen automatische mails. Geen provider outreach zonder GO. Geen klantdata-deling. Knoppen maken alleen interne to-do's of openen bestaande quote-handling flow.
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ir-kpis">
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open quotes</div>
          <div className="text-xl font-medium">{kpis?.open_quote_requests ?? '—'}</div>
        </div>
        <div className="bg-white border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Open EC leads</div>
          <div className="text-xl font-medium">{kpis?.open_ec_leads ?? '—'}</div>
        </div>
        <div className="bg-white border border-rose-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-rose-700">{`>`}48u oud</div>
          <div className="text-xl font-medium text-rose-900">{kpis?.older_than_48h_count ?? '—'}</div>
        </div>
        <div className="bg-white border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Won deze week</div>
          <div className="text-xl font-medium text-emerald-900">{kpis?.won_this_week ?? 0}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-700">Provider ontbreekt</div>
          <div className="text-xl font-medium text-amber-900">{summary.provider_missing_count ?? 0}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700">Installer-ready</div>
          <div className="text-xl font-medium text-emerald-900">{summary.installer_ready_count ?? 0}</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-purple-700">Blockers totaal</div>
          <div className="text-xl font-medium text-purple-900">{summary.blockers_total ?? 0}</div>
        </div>
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-primary">Pipeline waarde</div>
          <div className="text-xl font-medium text-primary">€{(summary.est_pipeline_value_eur || 0).toLocaleString('nl-NL')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 flex-wrap items-center" data-testid="ir-filters">
        <span className="text-[11px] text-muted-foreground">Categorie:</span>
        <button onClick={() => setFilterCategory('all')} className={`px-2 py-1 rounded-full text-[11px] border ${filterCategory === 'all' ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}>Alle</button>
        {Object.entries(categories).map(([key, meta]) => (
          <button key={key} onClick={() => setFilterCategory(key)} className={`px-2 py-1 rounded-full text-[11px] border ${filterCategory === key ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`} data-testid={`ir-filter-${key}`}>
            {meta.label}{summary.by_category?.[key] ? ` (${summary.by_category[key]})` : ''}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 flex-wrap items-center">
        <span className="text-[11px] text-muted-foreground">Urgency:</span>
        {['all', 'critical', 'high', 'elevated', 'normal'].map((u) => (
          <button key={u} onClick={() => setFilterUrgency(u)} className={`px-2 py-1 rounded-full text-[11px] border ${filterUrgency === u ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}>
            {u === 'all' ? 'Alle' : URGENCY_LABEL[u]}{u !== 'all' && summary.by_urgency?.[u] ? ` (${summary.by_urgency[u]})` : ''}
          </button>
        ))}
        <label className="ml-2 flex items-center gap-1 text-[11px] cursor-pointer">
          <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} data-testid="ir-include-closed" />
          Toon gesloten leads
        </label>
      </div>

      {/* Cards */}
      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">Geen leads met deze filters.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ir-cards">
          {items.map((it) => (
            <div key={`${it.source}-${it.id}`} className="bg-white border border-border rounded-lg p-3 space-y-1.5" data-testid={`ir-card-${it.id}`}>
              <div className="flex items-center justify-between gap-1">
                <strong className="text-sm truncate">{it.name}</strong>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${URGENCY_COLOR[it.urgency]}`}>{URGENCY_LABEL[it.urgency]}</span>
              </div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded">{it.category_label}</span>
                <span>{it.source === 'quote_request' ? 'Offerteaanvraag' : 'EC lead'}</span>
                <span>·</span>
                <span>{it.postcode || it.city || '—'}</span>
                {it.hours_old !== null && <span>· {it.hours_old}u oud</span>}
              </div>
              <div className="text-[11px]">Status: <code className="bg-muted px-1 rounded">{it.status}</code> · Est: <strong>€{it.est_value_eur.toLocaleString('nl-NL')}</strong></div>

              {it.blockers.length > 0 && (
                <div className="flex items-start gap-1 text-[10px]">
                  <AlertTriangle className="w-3 h-3 text-rose-600 mt-0.5 flex-shrink-0" />
                  <div className="flex flex-wrap gap-1">
                    {it.blockers.map((b) => (
                      <span key={b} className="bg-rose-50 text-rose-800 border border-rose-200 px-1 rounded">{BLOCKER_LABEL[b] || b}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded p-1.5 text-[11px] text-blue-900">
                <strong>Aanbevolen:</strong> {it.recommended_action}
              </div>

              <div className="flex gap-1 flex-wrap pt-1">
                <button onClick={() => generateTodo(it)} disabled={busy === `todo-${it.id}`} className="px-2 py-1 text-[11px] btn-primary rounded flex items-center gap-1" data-testid={`ir-todo-${it.id}`}>
                  {busy === `todo-${it.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />} Maak to-do
                </button>
                {it.source === 'quote_request' && (
                  <a href={`#quote_requests`} onClick={() => { try { window.dispatchEvent(new CustomEvent('admin:navigate-tab', { detail: 'quote_requests' })); } catch(_){} }} className="px-2 py-1 text-[11px] border border-border rounded hover:bg-muted">Open in Quote Requests</a>
                )}
                {it.source === 'ec_lead' && (
                  <a href={`#energy_control_leads`} className="px-2 py-1 text-[11px] border border-border rounded hover:bg-muted">Open in EC Leads</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-muted-foreground italic flex items-center gap-1 pt-2">
        <ShieldCheck className="w-3 h-3" /> Read-only aggregator. Externe acties blijven in Quote Requests tab achter expliciete admin approval. Geen mails, geen provider outreach, geen klantdata-deling, geen klantfunnel/Ads/Stripe/Daisycon/Homey/DIA wijzigingen.
      </div>
    </div>
  );
}
